import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type ModeratorPermission =
  | "moderate_reviews"
  | "manage_contacts"
  | "view_orders"
  | "manage_blog"
  | "manage_faq";

export const ALL_MOD_PERMISSIONS: ModeratorPermission[] = [
  "moderate_reviews",
  "manage_contacts",
  "view_orders",
  "manage_blog",
  "manage_faq",
];

export const MOD_PERMISSION_LABELS: Record<ModeratorPermission, string> = {
  moderate_reviews: "Moderate product reviews",
  manage_contacts: "Manage contact submissions",
  view_orders: "View orders (read-only)",
  manage_blog: "Manage blog articles",
  manage_faq: "Manage FAQ items",
};

export interface UserRolesState {
  isAdmin: boolean;
  isModerator: boolean;
  permissions: Set<ModeratorPermission>;
  isLoading: boolean;
  /** True if user holds the permission OR is an admin */
  can: (perm: ModeratorPermission) => boolean;
  /** True if user has access to ANY admin-panel area (admin or any mod perm) */
  hasAnyAdminAccess: boolean;
}

export const useUserRoles = (): UserRolesState => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isModerator, setIsModerator] = useState(false);
  const [permissions, setPermissions] = useState<Set<ModeratorPermission>>(new Set());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setIsAdmin(false);
          setIsModerator(false);
          setPermissions(new Set());
          return;
        }

        const [{ data: roles }, { data: perms }] = await Promise.all([
          supabase.from("user_roles").select("role").eq("user_id", user.id),
          supabase
            .from("moderator_permissions")
            .select("permission")
            .eq("user_id", user.id),
        ]);

        const roleSet = new Set((roles || []).map((r) => r.role));
        setIsAdmin(roleSet.has("admin"));
        setIsModerator(roleSet.has("moderator"));
        setPermissions(
          new Set((perms || []).map((p) => p.permission as ModeratorPermission))
        );
      } catch {
        setIsAdmin(false);
        setIsModerator(false);
        setPermissions(new Set());
      } finally {
        setIsLoading(false);
      }
    };

    load();
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => load());
    return () => subscription.unsubscribe();
  }, []);

  const can = (perm: ModeratorPermission) => isAdmin || permissions.has(perm);
  const hasAnyAdminAccess = isAdmin || permissions.size > 0;

  return { isAdmin, isModerator, permissions, isLoading, can, hasAnyAdminAccess };
};
